import type {
  HTMLAttributes,
  ReactNode,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  CircleAlert,
  RefreshCw,
} from 'lucide-react'

import { Button } from './button'
import { cn } from '@/shared/lib/cn'

export interface ErrorStateProps
  extends Omit<
    HTMLAttributes<HTMLDivElement>,
    'title'
  > {
  /**
   * Optional override. Uses the translated generic
   * error title when omitted.
   */
  title?: ReactNode

  /**
   * Optional override. Uses the translated generic
   * error description when omitted.
   *
   * Pass null to intentionally hide the description.
   */
  description?: ReactNode

  action?: ReactNode

  onRetry?: () => void

  /**
   * Optional override for the retry button.
   */
  retryLabel?: string

  /**
   * Optional override for the retrying state.
   */
  retryingLabel?: string

  isRetrying?: boolean
  compact?: boolean
}

export function ErrorState({
  title,
  description,
  action,
  onRetry,
  retryLabel,
  retryingLabel,
  isRetrying = false,
  compact = false,
  className,
  ...props
}: ErrorStateProps) {
  const { t } = useTranslation()

  const resolvedTitle =
    title ??
    t('ui.errorState.defaultTitle')

  /*
   * undefined means use the translated default.
   * null means intentionally show no description.
   */
  const resolvedDescription =
    description === undefined
      ? t(
          'ui.errorState.defaultDescription',
        )
      : description

  const resolvedRetryLabel =
    retryLabel ??
    t('actions.retry')

  const resolvedRetryingLabel =
    retryingLabel ??
    t('ui.loading.retrying')

  return (
    <div
      {...props}
      role="alert"
      aria-busy={
        isRetrying
          ? true
          : undefined
      }
      className={cn(
        [
          'flex flex-col items-center',
          'justify-center text-center',
          'rounded-xl border',
          'border-(--color-danger)/25',
          'bg-(--color-danger-muted)',
        ],
        compact
          ? 'px-5 py-6'
          : 'min-h-64 px-6 py-10',
        className,
      )}
    >
      <div
        className={[
          'flex h-12 w-12',
          'items-center justify-center',
          'rounded-full',
          'bg-(--color-danger-muted)',
          'text-(--color-danger)',
        ].join(' ')}
      >
        <CircleAlert
          aria-hidden="true"
          className="h-6 w-6"
        />
      </div>

      <h2 className="mt-4 text-base font-semibold text-(--color-foreground)">
        {resolvedTitle}
      </h2>

      {resolvedDescription ? (
        <p className="mt-2 max-w-lg text-sm leading-6 text-(--color-muted-foreground)">
          {resolvedDescription}
        </p>
      ) : null}

      {onRetry || action ? (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {onRetry ? (
            <Button
              variant="secondary"
              isDisabled={isRetrying}
              onPress={onRetry}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn(
                  'h-4 w-4',
                  isRetrying &&
                    'animate-spin',
                )}
              />

              {isRetrying
                ? resolvedRetryingLabel
                : resolvedRetryLabel}
            </Button>
          ) : null}

          {action}
        </div>
      ) : null}
    </div>
  )
}